import PropTypes from "prop-types";
import { useCallback, useState } from "react";

import styled from "styled-components/macro";

import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";

import FnrChevron from "UI/FnrChevron";
import { colors } from "UI/colors";

import AmountDisplay from "./AmountDisplay";
import { unformatUSD } from "./AmountDisplay/helpers";
import useAddOnGiftAmountCalculation from "./AmountDisplay/useAddOnGiftAmountCalculation";
import useDetermineAmountDisplayStatus from "./AmountDisplay/useDetermineAmountDisplayStatus";

export default function AddOnGiftMainContent({
  addOnGift,
  isExpanded,
  setIsExpanded,
}) {
  const {
    icon,
    title,
    addOnDescription,
    isRequired,
    addOnID,
    presetAmount,
    type,
  } = addOnGift;
  const {
    state: {
      fund: { fundColorSafe },
    },
    dispatch,
  } = usePaymentPage();
  const [hovering, setHovering] = useState(false);
  const [buttonHasBeenClicked, setButtonHasBeenClicked] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const displayStatus = useDetermineAmountDisplayStatus({
    addOnGift,
    buttonHasBeenClicked,
  });
  const { displayAmount, inputAmountUpdated, setInputAmountUpdated } =
    useAddOnGiftAmountCalculation(addOnGift);

  const isExpandable = Boolean(addOnDescription);

  const handleWrapperClick = () => {
    if (isExpandable && !isExpanded) setIsExpanded(true);
  };

  const saveAmount = useCallback(
    (amountOverride, checkedOverride) => {
      const useAmountOverride =
        Boolean(amountOverride) ||
        amountOverride === "0" ||
        amountOverride === 0;
      dispatch({
        type: "setAddOnAmount",
        payload: {
          addOnID,
          amount: unformatUSD(
            useAmountOverride ? amountOverride || 0 : displayAmount || 0
          ),
          isChecked:
            typeof checkedOverride === "boolean" ? checkedOverride : !isChecked,
          type,
        },
      });
    },
    [dispatch, addOnID, displayAmount, isChecked, type]
  );

  // const handleCheckboxToggle = (e) => {
  //   e.stopPropagation();
  //   saveAmount();
  //   setIsChecked((prev) => !prev);
  // };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    saveAmount(presetAmount || null);
    setIsChecked(false);
    setButtonHasBeenClicked(false);
  };

  const handleMouseEnter = () => {
    setHovering(true);
  };
  const handleMouseLeave = () => {
    setHovering(false);
  };

  return (
    <Wrapper
      onClick={handleWrapperClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      expandable={isExpandable && !isExpanded}
    >
      <HoverBackground hovering={hovering && isExpandable && !isExpanded} />
      <AddOnGiftIcon className={`fad ${icon}`} $color={fundColorSafe} />
      <TitleText $color={fundColorSafe}>
        <span>{title}</span>
        {isExpandable && (
          <ChevronButton
            active={isExpanded}
            $grayedOut={isRequired}
            $color={fundColorSafe}
          />
        )}
      </TitleText>
      <DeleteAndAmountHolder
        showingDeleteBtn={displayStatus !== "button" && !isRequired}
      >
        <DeleteButton
          $hovering={hovering}
          onClick={handleDeleteClick}
          show={displayStatus !== "button" && !isRequired}
        >
          <i className="far fa-trash-alt" />
        </DeleteButton>

        <AmountDisplay
          addOnGift={addOnGift}
          displayStatus={displayStatus}
          displayAmount={displayAmount}
          inputAmountUpdated={inputAmountUpdated}
          setInputAmountUpdated={setInputAmountUpdated}
          setButtonHasBeenClicked={setButtonHasBeenClicked}
          isChecked={isChecked}
          setIsChecked={setIsChecked}
          saveAmount={saveAmount}
        />
      </DeleteAndAmountHolder>
    </Wrapper>
  );
}

AddOnGiftMainContent.propTypes = {
  addOnGift: PropTypes.object,
  isExpanded: PropTypes.bool,
  setIsExpanded: PropTypes.func,
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 43px;
  ${(p) => p.expandable && "cursor: pointer;"}
  padding-right: 1px;
`;

const HoverBackground = styled.div`
  position: absolute;
  top: 6px;
  bottom: 6px;
  min-height: 38px;
  //width: 100%;
  width: calc(100% - 12px);
  border-radius: 8px;
  background: ${colors.fnLtGray};
  z-index: 1;

  transition: opacity 450ms linear;
  opacity: ${(p) => (p.hovering ? "0.6" : "0")};
`;

const AddOnGiftIcon = styled.div`
  height: 50px;
  width: 50px;
  border-radius: 50%;
  background: ${(p) => (p.$grayedOut ? "#9a9a9a" : p.$color)};
  position: absolute;
  //top: -31px;
  //left: -25px;
  top: -13px;
  left: -7px;
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  color: white;

  &[class*="fad fa-"]:after {
    content: "";
  }
`;

const TitleText = styled.div`
  flex-grow: 1;
  //padding-left: 56px;
  padding-left: 42px;
  padding-right: 9px;
  margin-right: 4px;
  color: ${(p) => (p.$grayedOut ? "#9a9a9a" : p.$color)};
  font-weight: 600;
  max-width: 198px;
  position: relative;
  z-index: 2;

  span {
    word-break: break-word;
    line-height: 19px;
  }

  display: flex;
  align-items: center;
`;

const DeleteAndAmountHolder = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.showingDeleteBtn ? "24px 1fr" : "auto")};
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 2;
`;

const DeleteButton = styled.div`
  display: ${(p) => (p.show ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  margin-left: 4px;
  line-height: 21px;
  font-size: 16px;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  padding: 1px;
  text-align: center;
  margin: 0 auto;
  color: ${colors.fnDkRed};

  transition: all 300ms linear;

  opacity: ${(p) => (p.$hovering ? "1" : "0")};

  &:hover {
    color: white;
    background: ${colors.fnDkRed};
  }

  &:active {
    transform: scale(0.9);
  }

  @media (pointer: none) {
    display: flex;
  }
`;

const ChevronButton = styled(FnrChevron)`
  //cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  opacity: 0.5;
  margin-left: 4px;
  color: ${(p) => (p.$grayedOut ? "#9a9a9a" : p.$color)};

  transition: transform 400ms linear;
  transition: opacity 400ms linear;

  ${(p) => p.active && "opacity: 0; transform: scale(0);"}
`;
